/**
 * "Tem uma versão nova — clique para atualizar."
 *
 * ### Por que é um BOTÃO e não um recarregamento automático
 *
 * Recarregar sozinho apaga o que a pessoa está fazendo: o lançamento de comissão digitado pela
 * metade, o nome da campanha, o filtro que ela levou um minuto para montar. O deploy é
 * conveniência nossa; a digitação é trabalho dela. Quem escolhe a hora é quem está na tela.
 *
 * ### Dois gatilhos, e o segundo é o que realmente dói
 *
 * 1. **Sondagem** de `version.json`: a cada 15 minutos e toda vez que a aba volta a ficar
 *    visível (com folga de 1 minuto entre leituras, para alternar aba não virar enxurrada).
 * 2. **`vite:preloadError`**: o navegador tentou buscar um pedaço de código que o deploy já
 *    apagou e a tela NÃO ABRIU. Aqui não é mais um aviso educado — é a explicação de uma falha
 *    que a pessoa acabou de ver, e por isso o texto muda.
 *
 * ### O que ele não faz
 *
 * Não avisa quando a leitura falha. Offline, servidor reiniciando no meio do deploy,
 * desenvolvimento (onde o arquivo nem existe): tudo isso é "não sei", e "não sei" nunca vira
 * "mudou" (invariante 12). Recarregar offline entregaria uma página em branco.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, cn } from '@mgdlead/ui';
import { RefreshCw } from 'lucide-react';

import { lerVersao, mudou } from '../lib/versao';

/** De quanto em quanto tempo perguntar, com a aba aberta e visível. */
const INTERVALO_MS = 15 * 60 * 1000;
/** Piso entre duas leituras. Sem ele, alternar abas dispararia uma leitura por troca. */
const FOLGA_MS = 60 * 1000;

export type MotivoDoAviso = 'versao-nova' | 'pedaco-faltando';

export function AvisoDeAtualizacao({ className }: { className?: string }) {
  const [motivo, setMotivo] = useState<MotivoDoAviso | null>(null);
  /** A versão que ESTE código está rodando: a primeira que conseguimos ler. */
  const versaoAtual = useRef<string | null>(null);
  const ultimaLeitura = useRef(0);

  const verificar = useCallback(async () => {
    const agora = Date.now();
    if (agora - ultimaLeitura.current < FOLGA_MS) return;
    ultimaLeitura.current = agora;

    const leitura = await lerVersao();
    if (leitura.estado !== 'lida') return;

    if (versaoAtual.current === null) {
      versaoAtual.current = leitura.build;
      return;
    }
    if (mudou(versaoAtual.current, leitura)) setMotivo('versao-nova');
  }, []);

  useEffect(() => {
    // A primeira leitura não passa pela folga: é ela que estabelece a linha de base.
    void lerVersao().then((leitura) => {
      if (leitura.estado === 'lida') {
        versaoAtual.current = leitura.build;
        ultimaLeitura.current = Date.now();
      }
    });

    const timer = setInterval(() => void verificar(), INTERVALO_MS);

    const aoVoltar = () => {
      if (document.visibilityState === 'visible') void verificar();
    };
    document.addEventListener('visibilitychange', aoVoltar);

    /*
      O Vite dispara este evento quando um `import()` de chunk falha — que é exatamente o que
      acontece com uma aba antiga depois do deploy. Não é palpite sobre versão: a tela já
      quebrou, e este aviso é a única coisa na página que explica o porquê.
    */
    const aoFaltarPedaco = () => setMotivo('pedaco-faltando');
    window.addEventListener('vite:preloadError', aoFaltarPedaco);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', aoVoltar);
      window.removeEventListener('vite:preloadError', aoFaltarPedaco);
    };
  }, [verificar]);

  if (motivo === null) return null;

  const quebrou = motivo === 'pedaco-faltando';

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        // Fica ACIMA do conteúdo e colado embaixo no celular, onde o polegar alcança —
        // e acima da barra de navegação inferior, para não cobrir o menu.
        'fixed inset-x-0 bottom-0 z-50 border-t p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]',
        'sm:inset-x-auto sm:bottom-4 sm:right-4 sm:max-w-sm sm:rounded-lg sm:border',
        // ⚠️ Fundo OPACO (`bg-surface`), não um tom translúcido: o aviso flutua por cima do
        // conteúdo, e com transparência o texto da página atravessa o texto do aviso — os dois
        // ficam ilegíveis ao mesmo tempo. Pego por screenshot em 390×844, não por teste.
        // A cor do estado vive na BORDA e na barra lateral, que não precisam de opacidade.
        'bg-surface shadow-lg',
        quebrou ? 'border-warning/60' : 'border-info/50',
        className,
      )}
    >
      <div className="flex flex-col gap-2">
        <p className="text-sm text-foreground">
          {quebrou ? (
            <>
              <strong className="font-medium">Esta aba está numa versão antiga.</strong> Uma parte
              do painel foi atualizada e não está mais disponível para esta página — por isso a
              tela não abriu.
            </>
          ) : (
            <>
              <strong className="font-medium">Tem uma versão nova do painel no ar.</strong> Você
              pode continuar o que está fazendo e atualizar quando quiser.
            </>
          )}
        </p>
        <Button
          variant="primary"
          block
          onClick={() => window.location.reload()}
          className="sm:w-auto sm:self-end"
        >
          <RefreshCw aria-hidden="true" className="size-4" />
          Atualizar agora
        </Button>
      </div>
    </div>
  );
}
